import { ISeminarTask } from "../models/tasks";

const getAllSelectedIds = (allUserSelections: { [taskId: string]: any[] }): Set<string> => {
    if (!allUserSelections) {
        return new Set();
    }

    const validSelections = Object.values(allUserSelections).flat().filter((selection: any) => {
        if (typeof selection === 'string') {
            return selection != null;
        }
        if (selection && typeof selection === 'object') {
            return Object.values(selection).some(value => value != null);
        }
        return false;
    });

    const allIds = validSelections.flatMap((selection: any) => {
        if (typeof selection === 'string') {
            return selection;
        }
        if (selection && typeof selection === 'object') {
            return Object.values(selection).filter(value => value != null);
        }
        return [];
    });

    return new Set(allIds.filter((id): id is string => typeof id === 'string'));
};

export const canCancelBlock = (
    blockId: string,
    block: { Block_Task_Dependencies1__r?: any[] },
    allUserSelections?: { [taskId: string]: any[] }
): boolean => {
    if (!block.Block_Task_Dependencies1__r) {
        return true;
    }

    const selectedIds = getAllSelectedIds(allUserSelections);

    const targetDependency = block.Block_Task_Dependencies1__r.find(dep => 
        dep.Selected_Block__c === blockId || dep.Block_option__c === blockId
    );
    
    if (!targetDependency) {
        return true;
    }

    if (targetDependency.Selected_Block__c === blockId) {
        const optionBlocks = block.Block_Task_Dependencies1__r.filter(dep => 
            dep.Selected_Block__c === blockId
        );
        
        return !optionBlocks.some(dep => selectedIds.has(dep.Block_option__c));
    }
    
    if (targetDependency.Block_option__c === blockId && targetDependency.Selected_Block__c) {
        return !selectedIds.has(targetDependency.Selected_Block__c);
    }

    return true;
};

export const getFilteredBlocks = (
    blocks: any[],
    allUserSelections: { [taskId: string]: any[] },
    task: ISeminarTask 
): any[] => {
    const selectedIds = getAllSelectedIds(allUserSelections);

    const allDependencies = blocks
        .filter(block => block.Block_Task_Dependencies1__r && block.Block_Task_Dependencies1__r.length > 0)
        .flatMap(block => block.Block_Task_Dependencies1__r);

    if (allDependencies.length === 0 || selectedIds.size === 0) {
        return blocks;
    }

    const selectedBlocksArray = Array.from(selectedIds);
    const optionBlockSets: Set<string>[] = [];

    selectedBlocksArray.forEach((selectedBlockId: string) => {
        const optionBlocksForThisSelected = allDependencies
            .filter((dependency: any) => dependency.Selected_Block__c === selectedBlockId)
            .map((dependency: any) => dependency.Block_option__c)
            .filter((id: string) => id !== undefined);
        
        if (optionBlocksForThisSelected.length > 0) {
            optionBlockSets.push(new Set(optionBlocksForThisSelected));
        }
    });

    let intersectionOptionBlocks: Set<string>;
    if (optionBlockSets.length === 0) {
        intersectionOptionBlocks = new Set();
    } else if (optionBlockSets.length === 1) {
        intersectionOptionBlocks = optionBlockSets[0];
    } else {
        intersectionOptionBlocks = new Set(optionBlockSets[0]);
        for (let i = 1; i < optionBlockSets.length; i++) {
            intersectionOptionBlocks = new Set(
                Array.from(intersectionOptionBlocks).filter(blockId => 
                    optionBlockSets[i].has(blockId)
                )
            );
        }
    }

    let unionOptionBlocks: Set<string> = new Set();
    optionBlockSets.forEach(set => {
        set.forEach(blockId => unionOptionBlocks.add(blockId));
    });

    const optionBlocksToShow = intersectionOptionBlocks.size > 0 
        ? intersectionOptionBlocks 
        : unionOptionBlocks;

    const dependenciesSelectedBlockIds = allDependencies
        .map((dependency: any) => dependency?.Selected_Block__c)
        .filter((id: string) => id !== undefined);
    const dependenciesOptionBlockIds = allDependencies
        .map((dependency: any) => dependency?.Block_option__c)
        .filter((id: string) => id !== undefined);

    const dependenciesSelectedBlockIdsSet = new Set(dependenciesSelectedBlockIds);
    const dependenciesOptionBlockIdsSet = new Set(dependenciesOptionBlockIds);

    return blocks.filter((block: any) => {
        let shouldShow = true;

        if (selectedIds.has(block.Id)) {
            return true;
        }

        const isOptionBlockToShow = optionBlocksToShow.has(block.Id);

        if (isOptionBlockToShow) {
            return true;
        }

        dependenciesSelectedBlockIdsSet.forEach((selectedBlockId: string) => {
            if (selectedIds.has(selectedBlockId)) {
                shouldShow = false;
                return;
            }
        });

        dependenciesOptionBlockIdsSet.forEach((optionBlockId: string) => {
            if (selectedIds.has(optionBlockId) && task.Allow_Select_All__c) {
                shouldShow = true;
                return;
            }
        });

        return shouldShow;
    });
};
